import _ from 'lodash';
import { routeTo } from 'redux-router-kit';

import { getGravatarUrl, andify } from '@zapier/common-utils';
import {
  DEFAULT_LOGIN_URL,
  DEFAULT_SIGN_UP_URL,
  SignupType,
} from '@zapier/auth';
import {
  EntityActions,
  commonErrors as Errors,
  RouterSelectors,
  buildEntityActions,
  buildEntityApi,
  buildEntitySelectors,
  entityUtils,
  hasErrorsOfType,
} from '@zapier/toolbox-redux-framework';
import { addTruthyParamsToUrl, stripTrailingSlash } from '@zapier/url-utils';
import { daysFromNow, getClientTimeZone } from '@zapier/date';
import { fetch, postJson } from '@zapier/toolbox-browser-fetch';
import {
  identifyToFullStory,
  isFullStoryInstalled,
  setFullStoryUserVars,
} from '@zapier/toolbox-tracking';

import * as Account from './Account';
import Tracking from '../utils/Tracking';
import { getIdentity } from '../utils/IdentityUtils';
import { getSwitchesFromState } from '../utils/SwitchesUtils';
import partialWithDefault from '../utils/partialWithDefault';

export const type = 'profile';

const identity = getIdentity();

export const api = buildEntityApi(
  type,
  path => {
    return addTruthyParamsToUrl(
      stripTrailingSlash(`/api/v3/profiles/${path}`),
      { identity }
    );
  },
  profileApi => {
    const { loadCollection } = profileApi;

    profileApi.loadCollection = (...args) =>
      loadCollection(...args).catch(
        _.partial(entityUtils.remove401StatusFromGetErrorResponse, type)
      );

    profileApi.sendConfirmationEmail = (entityId, formData) => {
      return postJson(
        profileApi.modelEndpoint(`/${entityId}/confirm-email`),
        formData
      ).then(() => ({ id: entityId })); // entity code expects id to be returned
    };

    profileApi.createThroughGoogleOneTap = async data => {
      try {
        const response = await postJson('/google-sso/onetap/', data);
        return response;
      } catch (e) {
        // postJson doesn't parse the JSON response when there is an error code
        // and leaves the stream locked so you have to manually process responseText
        const json = JSON.parse(e.responseText);
        // If the login failed because of SAML then the sso_url will be returned with
        // the error text
        if (json.sso_url) {
          // Redirect to the sso_url so the user can log in with SAML
          window.location = json.sso_url;
        }
      }
      return null;
    };

    profileApi.create = data => {
      // "Trying too often" type failures 401 so we need `autoPromiseMiddleware`
      // to ignore those and not redirect to login while trying to sign up.
      return postJson('/api/v3/profiles', data).catch(
        _.partial(entityUtils.remove401StatusFromGetErrorResponse, type)
      );
    };

    const normalizeGDPRSuccess = async response => {
      try {
        const message = await response.json();
        return {
          status: 'success',
          statusCode: response.status,
          // if it's a 404, there's basically nothing to report,
          // so don't update the UI unnecessarily for that. For all
          // other error messages though, we should let them know what
          // happened.
          // the notification needs to be separate from the
          // status.
          ...message,
        };
      } catch (err) {
        return {
          status: 'success',
        };
      }
    };

    const normalizeGDPRError = async response => {
      try {
        const message = response.bodyUsed
          ? JSON.parse(response.responseText)
          : await response.json();
        return {
          status: 'error',
          statusCode: response.status,
          error: _.head(message.errors),
        };
      } catch (err) {
        return {
          status: 'error',
          statusCode: response.status,
          error: err,
        };
      }
    };

    profileApi.checkGDPRItemStatus = (actionType, id) => {
      return fetch(profileApi.modelEndpoint(`${id}/data/${actionType}`))
        .then(normalizeGDPRSuccess)
        .catch(normalizeGDPRError);
    };

    profileApi.checkExportStatus = _.partial(
      profileApi.checkGDPRItemStatus,
      'export'
    );

    profileApi.checkDeleteStatus = _.partial(
      profileApi.checkGDPRItemStatus,
      'delete'
    );

    profileApi.runGDPRAction = (actionType, id) => {
      return fetch(profileApi.modelEndpoint(`${id}/data/${actionType}`), {
        method: 'POST',
      })
        .then(normalizeGDPRSuccess)
        .catch(normalizeGDPRError);
    };

    profileApi.exportData = _.partial(profileApi.runGDPRAction, 'export');

    profileApi.deleteData = _.partial(profileApi.runGDPRAction, 'delete');
  }
);

export const ALERT_ACTIONS = 'alert_actions';
export const ALERT_TRIGGERS = 'alert_triggers';
export const AVATAR_URL = 'avatar'; // client-side only
export const DISABLE_SSL = 'disable_ssl_check';
export const EMAIL = 'email';
export const EMAIL_CONFIRMED = 'email_confirmed';
export const FIRST_NAME = 'first_name';
export const LAST_NAME = 'last_name';
export const IS_DEVELOPER = 'is_developer';
export const NEW_PASSWORD = 'password1';
export const NEW_PASSWORD_CONFIRM = 'password2';
export const PASSWORD_VERIFY = 'password';
export const SUMMARY_SCHEDULE = 'summary_schedule';
export const TIMEZONE = 'timezone';
export const TOS_AGREEMENT = 'tos_agreement';
export const SHOULD_RENEW_TOS = 'should_renew_tos';
export const EMAIL_PENDING_CHANGE = 'email_pending_change';
export const EMAIL_CONFIRMATION_SENT = 'email_confirm_sent';
export const VIEWED_HELP = 'viewed_help';
export const LOADING = 'Loading…';
export const GOOGLE_ONE_TAP_VERIFY_MESSAGE = 'account_verification_required';
export const GOOGLE_ONE_TAP_NEW_ENTITY_ID = 'new-profile-google-one-tap';
export const PERSONAS = 'personas';
export const JOB_TITLE = 'job_title';
export const PRIMARY_ORGANIZATION = 'primary_organization';

export const selectors = buildEntitySelectors(type, sel => {
  sel.shouldAlertActions = _.partial(sel.dataItem, ALERT_ACTIONS);
  sel.shouldAlertTriggers = _.partial(sel.dataItem, ALERT_TRIGGERS);
  sel.isSslDisabled = _.partial(sel.dataItem, DISABLE_SSL);
  sel.code = _.partial(sel.dataItem, 'code');
  sel.email = _.partial(sel.dataItem, EMAIL);
  sel.emailHash = _.partial(sel.dataItem, 'email_hash');
  sel.entrustedAppIds = _.partial(sel.dataItem, 'entrusted_app_ids');
  sel.gravatarUrl = _.flow(sel.emailHash, getGravatarUrl);
  sel.isEmailConfirmed = _.partial(sel.dataItem, EMAIL_CONFIRMED);
  sel.isEmailPendingChange = _.partial(sel.stateItem, EMAIL_PENDING_CHANGE);
  sel.isEmailConfirmationSent = _.partial(
    sel.stateItem,
    EMAIL_CONFIRMATION_SENT
  );
  sel.firstName = partialWithDefault(sel.dataItem, [FIRST_NAME], '');
  sel.lastName = partialWithDefault(sel.dataItem, [LAST_NAME], '');
  sel.personas = partialWithDefault(sel.dataItem, [PERSONAS], []);
  sel.jobTitle = partialWithDefault(sel.dataItem, [JOB_TITLE], '');
  sel.summarySchedule = _.partial(sel.dataItem, SUMMARY_SCHEDULE);
  sel.timezone = _.partial(sel.dataItem, TIMEZONE);
  sel.primaryOrganizationId = _.partial(sel.dataItem, PRIMARY_ORGANIZATION);
  sel.all.currentPrimaryOrgId = state => {
    const currentProfile = sel.all.currentProfile(state);
    return sel.primaryOrganizationId(currentProfile);
  };
  sel.lastPasswordResetToken = _.partial(
    sel.dataItem,
    'last_password_reset_token'
  );
  sel.isDeveloper = _.partial(sel.dataItem, IS_DEVELOPER);
  sel.isHighValue = _.partial(sel.dataItem, 'is_high_value');
  sel.isStaff = _.partial(sel.dataItem, 'is_staff');
  sel.isZtReviewer = _.partial(sel.dataItem, 'is_zt_reviewer');
  sel.tosAgreement = _.partial(sel.dataItem, TOS_AGREEMENT);
  sel.hasAcceptedTerms = entity => !sel.dataItem(SHOULD_RENEW_TOS, entity);
  sel.hasEnabled2fa = _.partial(sel.dataItem, 'enable_totp_2fa');
  sel.isGDPRConsented = partialWithDefault(
    sel.dataItem,
    ['is_gdpr_consented'],
    false
  );
  sel.isSwitchOn = (switchName, entity) =>
    sel.dataItem(`switches.${switchName}`, entity) === true ||
    Boolean(localStorage.getItem(switchName));
  sel.viewedHelp = _.partial(sel.dataItem, VIEWED_HELP);
  sel.hasViewedHelp = (key, entity) =>
    _.get(sel.viewedHelp(entity), key) === true;
  sel.seenNotifications = partialWithDefault(
    sel.dataItem,
    ['has_seen_notifications'],
    {}
  );
  sel.hasSeenNotification = (key, entity) =>
    _.get(sel.seenNotifications(entity), key) === true;
  sel.hasSeenJobsCta = partialWithDefault(
    sel.hasSeenNotification,
    ['jobs_cta'],
    false
  );
  sel.shouldSeeNPSSurvey = partialWithDefault(
    sel.dataItem,
    ['should_see_nps_survey'],
    false
  );
  sel.normalizedName = entity =>
    `${sel.firstName(entity)} ${sel.lastName(entity)}`.trim() ||
    sel.email(entity);
  sel.normalizedNameOrLoading = entity => sel.normalizedName(entity) || LOADING;
  sel.signup = _.partial(sel.dataItem, 'signup');
  sel.hasSignedUpInLastDays = (entity, days) =>
    daysFromNow(sel.signup(entity)) <= days;
  sel.hasActivated = _.partial(sel.dataItem, 'has_activated');
  sel.bannerMessage = partialWithDefault(sel.dataItem, ['banner_message'], '');

  sel.exportData = _.partial(sel.dataItem, 'export_data');
  sel.exportDataStatus = entity => _.get(sel.exportData(entity), 'status');
  sel.exportDataMessage = entity => _.get(sel.exportData(entity), 'message');
  sel.isExportInProgress = entity =>
    _.get(sel.exportData(entity), 'in_progress');
  sel.isExportComplete = entity => _.get(sel.exportData(entity), 'is_complete');

  sel.deleteData = _.partial(sel.dataItem, 'delete_data');
  sel.deleteDataStatus = entity => _.get(sel.deleteData(entity), 'status');
  sel.deleteDataMessage = entity => _.get(sel.deleteData(entity), 'message');
  sel.isDeleteInProgress = entity =>
    _.get(sel.deleteData(entity), 'in_progress');
  sel.isDeleteComplete = entity => _.get(sel.deleteData(entity), 'is_complete');

  // Is the user timezone mismatching his computer's one?
  sel.isTimezoneMismatching = entity => {
    const profileTimeZone = sel.timezone(entity);
    const clientTimeZone = getClientTimeZone();

    return profileTimeZone ? profileTimeZone !== clientTimeZone : false;
  };
  sel.roles = _.partial(sel.dataItem, 'roles');
  sel.canSharePrivately = partialWithDefault(
    sel.isSwitchOn,
    ['sharing_private_sharing_enabled'],
    false
  );
  sel.canSharePublicly = partialWithDefault(
    sel.isSwitchOn,
    ['sharing_public_sharing_enabled'],
    false
  );
  sel.canShareGlobally = partialWithDefault(
    sel.isSwitchOn,
    ['sharing_global_sharing_enabled'],
    false
  );
  sel.canEmbedShare = partialWithDefault(
    sel.isSwitchOn,
    ['sharing_can_embed_share'],
    false
  );

  sel.all.currentProfileId = _.partial(sel.all.dataItem, 'active_id');
  sel.all.currentProfile = state =>
    sel.all.entity(sel.all.currentProfileId(state), state);
  // This uses `Account` but the tracking key is for `Profile`,
  // so it's a `Profile` selector.
  sel.all.currentAccountDowngradedTrackingKey = state =>
    `account-downgraded-${Account.selectors.all.currentAccountId(state)}`;
  sel.all.isLoggedIn = state => !!sel.all.currentProfile(state);
  sel.all.shouldLoad = state => {
    // When we hydrate the state of the users logged in status from the backend
    // we do not hydrate all of the information. Instead we only send enough
    // info for the application to know it is logged in (see header.html).
    // This check here ensures that we then request the full profile data once
    // we are on the client.
    if (sel.all.isPartiallyHydrated(state)) {
      return true;
    }

    return andify(
      _.negate(sel.all.isFailure),
      _.negate(sel.all.isLoggedIn)
    )(state);
  };
  sel.all.googleOneTapEntity = _.partial(
    sel.all.entity,
    GOOGLE_ONE_TAP_NEW_ENTITY_ID
  );
  sel.all.needsGoogleSSOAccountVerification = state =>
    _.includes(
      sel.statusMessages(sel.all.googleOneTapEntity(state)),
      GOOGLE_ONE_TAP_VERIFY_MESSAGE
    );
  // Google One tap might have an error stating that it needs to verify the
  // account. That will be handled by `needsGoogleSSOAccountVerification`. This
  // is for detecting any other error that may occur.
  sel.all.hasGoogleOneTapError = state => {
    const isGoogleOneTapErrored = sel.isFailure(
      sel.all.googleOneTapEntity(state)
    );
    return Boolean(
      isGoogleOneTapErrored && !sel.all.needsGoogleSSOAccountVerification(state)
    );
  };

  sel.all.isAdminOf = (accountId, state) => {
    const currentProfile = sel.all.currentProfile(state);
    const roles = sel.roles(currentProfile);
    if (Array.isArray(roles)) {
      return roles.some(
        r =>
          r.account_id === accountId &&
          (r.role === 'owner' || r.role === 'admin')
      );
    }
    return false;
  };

  sel.all.isOwnerOf = (accountId, state) => {
    const currentProfile = sel.all.currentProfile(state);
    const roles = sel.roles(currentProfile);
    if (Array.isArray(roles)) {
      return roles.some(r => r.account_id === accountId && r.role === 'owner');
    }
    return false;
  };

  sel.all.isMasquerading = partialWithDefault(
    sel.all.dataItem,
    ['is_masquerading'],
    false
  );

  sel.all.isAdmin = state => {
    const accountId = Account.selectors.all.currentAccountId(state);
    return sel.all.isAdminOf(accountId, state);
  };

  sel.all.hasEnableManageTeamTab = state =>
    sel.isSwitchOn('enable_manage_team_tab', sel.all.currentProfile(state));

  sel.all.shouldFireAppSearchEvent = state =>
    sel.isSwitchOn(
      'editor_emit_event_app_search',
      sel.all.currentProfile(state)
    );

  sel.all.shouldShowExpandedEventPicker = state =>
    sel.isSwitchOn(
      'Editor-Expanded_Event_Picker-Opt_In',
      sel.all.currentProfile(state)
    ) &&
    !sel.isSwitchOn(
      'Editor-Expanded_Event_Picker-Opt_Out',
      sel.all.currentProfile(state)
    );
});

export const utils = {
  createSignupError(profile) {
    const errors = selectors.statusMessages(profile);
    if (hasErrorsOfType('field_errors', errors)) {
      return new Errors.SignUpValidationError({ errors });
    } else {
      return new Errors.SignUpError({ errors });
    }
  },
};

export const actions = buildEntityActions(
  type,
  selectors,
  api,
  entityActions => {
    const overrideLoad = originalActionCreator => {
      return (...args) => (dispatch, getState) => {
        return dispatch(originalActionCreator(...args)).then(() => {
          const state = getState();

          if (isFullStoryInstalled()) {
            const switches = getSwitchesFromState(state);
            setFullStoryUserVars({
              switches_str: JSON.stringify(switches),
            });
          }
        });
      };
    };

    entityActions.loadCollection = overrideLoad(entityActions.loadCollection);
    entityActions.forceLoadCollection = overrideLoad(
      entityActions.forceLoadCollection
    );

    const getEntityData = (id, state) =>
      selectors.data(selectors.all.entity(id, state));

    entityActions.sendConfirmationEmail = (messages, entityId) => {
      return (dispatch, getState) => {
        return dispatch(
          EntityActions.saveEntity(
            type,
            () => {},
            api.sendConfirmationEmail,
            messages,
            entityId
          )
        ).then(() => {
          const profileEntity = selectors.all.entity(entityId, getState());

          dispatch(
            entityActions.markConfirmationSent(
              selectors.isSuccess(profileEntity),
              entityId
            )
          );
        });
      };
    };

    entityActions.sendEmailChangeRequest = (messages, entityId, formData) => {
      return (dispatch, getState) => {
        return dispatch(
          EntityActions.saveEntity(
            type,
            () => formData,
            api.sendConfirmationEmail,
            messages,
            entityId
          )
        ).then(() => {
          const profileEntity = selectors.all.entity(entityId, getState());
          const isSuccess = selectors.isSuccess(profileEntity);

          dispatch(entityActions.markAsEmailPending(isSuccess, entityId));

          return isSuccess;
        });
      };
    };

    entityActions.createThroughGoogleOneTap = (data, tempId) =>
      EntityActions.createEntity(
        type,
        () => data,
        api.createThroughGoogleOneTap,
        null,
        tempId
      );

    entityActions.saveProfile = (messages, entityId) =>
      EntityActions.saveEntity(
        type,
        getEntityData,
        api.save,
        messages,
        entityId
      );

    entityActions.deleteProfile = (messages, verificationData, id) =>
      EntityActions.deleteEntity(
        type,
        () => verificationData,
        api.delete,
        messages,
        id
      );

    entityActions.acknowledgeNotification = (notificationName, id) =>
      EntityActions.saveEntity(
        type,
        () => ({ has_seen_notifications: { [notificationName]: true } }),
        api.save,
        null,
        id
      );

    entityActions.acknowledgeNPSSurvey = id =>
      EntityActions.saveEntity(
        type,
        () => ({ should_see_nps_survey: false }),
        api.save,
        null,
        id
      );

    entityActions.acknowledgeHelp = (trackingKey, id) =>
      EntityActions.saveEntity(
        type,
        () => ({ [VIEWED_HELP]: { [trackingKey]: true } }),
        api.save,
        null,
        id
      );

    // We need the passwords to be in `data` when the user changes
    // his/her password or when the password needs to be supplied
    // for verification, but we probably shouldn't hold onto that
    // data for longer than we need to.
    entityActions.clearPasswords = id =>
      entityActions.updateEntityData(
        {
          [NEW_PASSWORD]: undefined,
          [NEW_PASSWORD_CONFIRM]: undefined,
          [PASSWORD_VERIFY]: undefined,
        },
        id
      );

    entityActions.signUp = data => (dispatch, getState) => {
      const newProfileId = 'new-profile';
      if (selectors.all.entity(newProfileId, getState())) {
        dispatch(entityActions.updateEntityData(data, newProfileId));
      } else {
        const entityData = { ...data, id: newProfileId };
        dispatch(entityActions.createCollectionUiEntity(entityData));
      }
      const cid = selectors.cid(selectors.all.entity(newProfileId, getState()));
      return Promise.resolve()
        .then(() => dispatch(entityActions.createEntity(null, newProfileId)))
        .then(() => {
          const newProfile = selectors.all.entityByCid(cid, getState());
          if (selectors.isFailure(newProfile)) {
            throw utils.createSignupError(newProfile);
          }

          const useCaseSlug = RouterSelectors.getQueryParam(
            getState(),
            'use_case'
          );

          Tracking.trackSignupConversion(SignupType.direct, {
            userId: selectors.id(newProfile),
            useCaseSlug,
          });
        })
        .then(() => {
          const id = selectors.id(selectors.all.entityByCid(cid, getState()));

          const clientTimeZone = getClientTimeZone();
          if (clientTimeZone) {
            dispatch(
              actions.updateEntityDataItem(TIMEZONE, clientTimeZone, id)
            );
            dispatch(actions.saveProfile(null, id));
          }
        });
    };

    entityActions.reloadCurrentProfile = () => (dispatch, getState) =>
      dispatch(
        entityActions.loadEntity(
          null,
          selectors.all.currentProfileId(getState())
        )
      );

    entityActions.googleOneTapSignup = (idToken, next) => (
      dispatch,
      getState
    ) => {
      const entityData = { id: GOOGLE_ONE_TAP_NEW_ENTITY_ID };

      // Need to create entity first to get its `cid`.
      dispatch(entityActions.createCollectionUiEntity(entityData));
      const cid = selectors.cid(
        selectors.all.entity(GOOGLE_ONE_TAP_NEW_ENTITY_ID, getState())
      );

      return Promise.resolve()
        .then(() =>
          dispatch(
            entityActions.createThroughGoogleOneTap(
              { idToken },
              GOOGLE_ONE_TAP_NEW_ENTITY_ID
            )
          )
        )
        .then(() => {
          const state = getState();

          // The user may need to verify their account, in that case forward
          // them to the verification route
          if (selectors.all.needsGoogleSSOAccountVerification(state)) {
            return dispatch(routeTo('/google-sso/onetap-verify/'));
          }

          // Catch any other sort of error that may occur during the creation
          // flow.
          const googleOneTapEntity = selectors.all.googleOneTapEntity(state);
          if (selectors.isFailure(googleOneTapEntity)) {
            const statusMessages = selectors.statusMessages(googleOneTapEntity);
            throw statusMessages;
          }

          // This can be a newly created profile (if this google e-mail didn't already have an account at Zapier) or
          // a profile that already existed (if this e-mail already had an account – in which case we only link it to its google account)
          const signedInOrCreatedProfile = selectors.all.entityByCid(
            cid,
            state
          );

          const id = selectors.id(signedInOrCreatedProfile);

          // `new_signup` only exists when signing up via google onetap, and can be used to check if
          // a new profile was created or if we just associated this google account with an existing profile.
          const isNewSignup = selectors.dataItem(
            'new_signup',
            signedInOrCreatedProfile
          );

          const clientTimeZone = getClientTimeZone();
          // Set the timezone if this is a new signup and we are able to
          // detect a timezone in the browser
          if (isNewSignup && clientTimeZone) {
            dispatch(
              actions.updateEntityDataItem(TIMEZONE, clientTimeZone, id)
            );
            dispatch(actions.saveProfile(null, id));
          }

          if (isNewSignup) {
            const useCaseSlug = RouterSelectors.getQueryParam(
              state,
              'use_case'
            );

            Tracking.trackSignupConversion(SignupType.oneTap, {
              userId: id,
              useCaseSlug,
            });
          }

          // Respect any `next` query params that were passed in
          if (next) {
            return dispatch(routeTo(next));
          }

          return dispatch(
            routeTo(isNewSignup ? DEFAULT_SIGN_UP_URL : DEFAULT_LOGIN_URL)
          );
        });
    };

    // Used to mark the users email confirmation as pending, this is a
    // temporary client side fix so that we can show a confirmation pop up
    // to the user informing them they need to confirm the email after making
    // an email change request.
    entityActions.markAsEmailPending = _.partial(
      entityActions.updateEntityStateItem,
      EMAIL_PENDING_CHANGE
    );

    entityActions.markConfirmationSent = _.partial(
      entityActions.updateEntityStateItem,
      EMAIL_CONFIRMATION_SENT
    );

    entityActions.agreeToTermsOfService = id => dispatch => {
      dispatch(actions.updateEntityDataItem(TOS_AGREEMENT, true, id));
    };

    // Used to identify a user to FS in the interim between logging in and page reload.
    // (The django fullstory.html template identifies to FS upon page load.)
    entityActions.identifyToFullStory = () => {
      return (dispatch, getState) => {
        const profile = selectors.all.currentProfile(getState());

        if (isFullStoryInstalled() && profile) {
          identifyToFullStory(selectors.id(profile), {
            displayName: selectors.normalizedName(profile),
            email: selectors.email(profile),
          });
        }
      };
    };

    // /api/v3/profiles/XXX/data/delete or
    // /api/v3/profiles/XXX/data/export

    // 403: the feature is disabled by gargoyle
    // 404: feature is active but never been used
    // 409: feature already started
    // 202: feature is in progress
    // 200: feature is complete

    entityActions.updateGDPRData = (actionType, data) => {
      return (dispatch, getState) => {
        const profileId = selectors.all.currentProfileId(getState());
        const currentProfile = selectors.all.currentProfile(getState());
        const selectData = {
          export: selectors.exportData,
          delete: selectors.deleteData,
        }[actionType];
        const existingEntityData = selectData(currentProfile);
        const newData = {
          ...existingEntityData,
          ...data,
          // these keys are used specifically for the notification
          // on the page. Status must be either 'success' or 'failure'
          // and the explanation is the message.
          message: data.message || data.error,
          status: data.status,
        };
        dispatch(
          entityActions.updateEntityDataItem(
            `${actionType}_data`,
            newData,
            profileId
          )
        );
      };
    };

    entityActions.updateGDPRExportData = data => {
      return entityActions.updateGDPRData('export', data);
    };

    entityActions.updateGDPRDeleteData = data => {
      return entityActions.updateGDPRData('delete', data);
    };

    const GDPRApiFunction = {
      export: api.exportData,
      delete: api.deleteData,
    };

    const updateGDPRItemFunction = {
      export: entityActions.updateGDPRExportData,
      delete: entityActions.updateGDPRDeleteData,
    };

    entityActions.runGDPRAction = actionType => {
      return (dispatch, getState) => {
        const profileId = selectors.all.currentProfileId(getState());

        GDPRApiFunction[actionType](profileId).then(
          ({ status, statusCode, message, error }) => {
            dispatch(
              updateGDPRItemFunction[actionType]({
                status,
                // this will be either 201 or 202 depending on whether it's
                // a POST or GET respectively
                in_progress: statusCode === 201 || statusCode === 202,
                is_completed: statusCode === 200,
                message,
                error,
              })
            );
          }
        );
      };
    };

    entityActions.deleteData = () => entityActions.runGDPRAction('delete');

    entityActions.exportData = () => entityActions.runGDPRAction('export');

    entityActions.checkGDPRStatus = actionType => {
      const apiFunction = {
        export: api.checkExportStatus,
        delete: api.checkDeleteStatus,
      }[actionType];

      return (dispatch, getState) => {
        const profileId = selectors.all.currentProfileId(getState());
        apiFunction(profileId).then(data => {
          dispatch(
            updateGDPRItemFunction[actionType]({
              // this will be either 201 or 202 depending on whether it's
              // a POST or GET respectively
              in_progress: data.statusCode === 201 || data.statusCode === 202,
              is_completed: data.statusCode === 200,
            })
          );
        });
      };
    };

    entityActions.checkExportStatus = () =>
      entityActions.checkGDPRStatus('export');

    entityActions.checkDeleteStatus = () =>
      entityActions.checkGDPRStatus('delete');

    entityActions.clearGDPRNotification = actionType =>
      updateGDPRItemFunction[actionType]({ status: null, message: null });

    entityActions.clearDeleteStatus = () =>
      entityActions.clearGDPRNotification('delete');

    entityActions.clearExportStatus = () =>
      entityActions.clearGDPRNotification('export');
  }
);
